import { useEffect, useState } from 'react'

/**
 * Return `value` only after it has stopped changing for `delayMs`.
 *
 * For a search box, keep the raw state on the input so typing stays instant and
 * feed THIS to the query key. Without it every keystroke is a fresh request:
 * typing "susan" on the registrations list fired eight (a list + a status-count
 * per character), 14 of them in flight at once, and a query that runs in ~250ms
 * on its own took 4-8s in production because they queued behind each other.
 *
 * The trailing edge is what matters here - a query fired for "sus" is wasted the
 * moment "susa" arrives, so there is no value in a leading call.
 */
export function useDebouncedValue<T>(value: T, delayMs = 350): T {
  const [debounced, setDebounced] = useState(value)

  useEffect(() => {
    const handle = setTimeout(() => setDebounced(value), delayMs)
    return () => clearTimeout(handle)
  }, [value, delayMs])

  return debounced
}
